import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  emptyDocTrash,
  listDocTrash,
  purgeDocTrash,
  restoreDocTrash,
  type DocTrashEntry,
} from "@/lib/api";
import { Modal } from "@/components/Modal";
import { fmtDateTime, fmtRelative, fmtSize } from "./shared";

/**
 * Deleted items, newest first. A folder delete appears as one entry covering
 * everything it swept up, and restoring it puts the whole subtree back.
 */
export function TrashModal({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const trash = useQuery({ queryKey: ["docs-trash"], queryFn: listDocTrash });
  const [confirmPurge, setConfirmPurge] = useState<DocTrashEntry | "all" | null>(null);

  const done = () => {
    void qc.invalidateQueries({ queryKey: ["docs-trash"] });
    onChanged();
  };
  const restore = useMutation({ mutationFn: restoreDocTrash, onSuccess: done });
  const purge = useMutation({ mutationFn: purgeDocTrash, onSuccess: done });
  const empty = useMutation({ mutationFn: emptyDocTrash, onSuccess: done });

  const entries = trash.data ?? [];
  const busy = restore.isPending || purge.isPending || empty.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Trash"
      footer={
        <>
          <span className="mr-auto text-xs text-text-muted">
            Items are deleted for good 30 days after they're trashed.
          </span>
          {entries.length > 0 && (
            <button
              onClick={() => setConfirmPurge("all")}
              disabled={busy}
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-danger disabled:opacity-40"
            >
              Empty trash
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
          >
            Close
          </button>
        </>
      }
    >
      {trash.isLoading ? (
        <div className="grid place-items-center py-10 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin" />
        </div>
      ) : entries.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-muted">
          Trash is empty. Deleted files and folders land here and stay recoverable for 30 days.
        </p>
      ) : (
        <ul className="max-h-80 divide-y divide-border-light overflow-y-auto">
          {entries.map((e) => (
            <li key={e.id} className="flex items-center gap-3 py-2.5">
              <i
                className={`fa-solid ${e.kind === "folder" ? "fa-folder text-warning" : "fa-file text-text-muted"}`}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-text-heading">{e.name}</div>
                <div className="text-xs text-text-muted">
                  {e.kind === "folder"
                    ? `${e.itemCount} item${e.itemCount === 1 ? "" : "s"} inside`
                    : e.size != null
                      ? fmtSize(e.size)
                      : ""}
                  {" · deleted "}
                  {fmtDateTime(e.trashedAt)}
                  {e.trashedByName ? ` by ${e.trashedByName}` : ""}
                  {" · purges "}
                  {fmtRelative(e.purgesAt)}
                </div>
              </div>
              <button
                onClick={() => restore.mutate(e.id)}
                disabled={busy}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-text-secondary transition-colors hover:border-accent hover:text-text-heading disabled:opacity-40"
              >
                Restore
              </button>
              <button
                onClick={() => setConfirmPurge(e)}
                disabled={busy}
                title="Delete for good"
                className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-bg-elevated hover:text-danger disabled:opacity-40"
              >
                <i className="fa-solid fa-xmark text-xs" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Purging is the only irreversible action in the hub — always confirm. */}
      <Modal
        open={!!confirmPurge}
        onClose={() => setConfirmPurge(null)}
        title={confirmPurge === "all" ? "Empty trash" : "Delete for good"}
        footer={
          <>
            <button
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
              onClick={() => setConfirmPurge(null)}
            >
              Cancel
            </button>
            <button
              className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ background: "var(--color-danger)" }}
              onClick={() => {
                if (confirmPurge === "all") empty.mutate();
                else if (confirmPurge) purge.mutate(confirmPurge.id);
                setConfirmPurge(null);
              }}
            >
              Delete permanently
            </button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          {confirmPurge === "all" ? (
            <>
              Permanently delete all{" "}
              <strong className="text-text-heading">{entries.length} items</strong> in the
              trash?
            </>
          ) : (
            <>
              Permanently delete{" "}
              <strong className="text-text-heading">“{confirmPurge?.name}”</strong>
              {confirmPurge?.kind === "folder" ? " and everything inside it" : ""}
              ?
            </>
          )}{" "}
          This cannot be undone.
        </p>
      </Modal>
    </Modal>
  );
}
